import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, MapPin, Phone, Star, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`w-4 h-4 ${s <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      ))}
    </div>
  );
}

export default function WelcomePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [landing, setLanding] = useState(null);
  const [packages, setPackages] = useState(null);
  const [showActivate, setShowActivate] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateForm, setActivateForm] = useState({ email: "", password: "", confirmPassword: "" });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/leads/by-token/${token}`);
        setData(res.data);
        if (res.data.landing) setLanding(res.data.landing);
        if (res.data.lead?.business_email) {
          setActivateForm((f) => ({ ...f, email: res.data.lead.business_email }));
        }
      } catch (e) {
        if (e?.response?.status === 410) setError("Ten link wygasl. Skontaktuj sie z nami zeby otrzymac nowy.");
        else if (e?.response?.status === 404) setError("Ten link jest nieprawidlowy.");
        else setError("Nie udalo sie zaladowac strony.");
      }
    })();
    (async () => {
      try {
        const res = await axios.get(`${API}/packages`);
        setPackages(res.data);
      } catch (e) {
        console.error("Failed to fetch packages:", e);
      }
    })();
  }, [token]);

  const generateLanding = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/leads/by-token/${token}/generate`);
      setLanding(res.data);
      toast.success("Twoja strona jest gotowa!");
      setTimeout(() => {
        document.getElementById('preview')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    } catch (e) {
      toast.error("Nie udalo sie wygenerowac strony. Sprobuj ponownie.");
    } finally {
      setGenerating(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    if (activateForm.password !== activateForm.confirmPassword) {
      toast.error("Hasla nie sa identyczne");
      return;
    }
    setActivating(true);
    try {
      try {
        await register(activateForm.email, activateForm.password, data.business.name);
      } catch (regErr) {
        if (regErr?.response?.status === 400) {
          await login(activateForm.email, activateForm.password);
        } else {
          throw regErr;
        }
      }

      const response = await axios.post(
        `${API}/payments/checkout?business_id=${data.business.business_id}&package_tier=starter`,
        {},
        { withCredentials: true, headers: { 'origin': window.location.origin } }
      );

      if (response.data.mock) {
        navigate(`/payment/mock?payment_id=${response.data.payment_id}&business_id=${data.business.business_id}`);
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udalo sie aktywowac strony. Sprobuj ponownie.");
    } finally {
      setActivating(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-3">
            <h1 className="text-xl font-bold text-slate-900">{error}</h1>
            <p className="text-sm text-slate-600">
              <Link to="/" className="text-emerald-700 hover:underline">Przejdz do lokalnie.pro</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const { lead, business } = data;
  const photo = business.photos?.[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="font-bold text-emerald-700">lokalnie.pro</Link>
          <Badge variant="outline" className="text-xs">Twoja oferta</Badge>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 pt-12 pb-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm mb-3">
            <Sparkles className="w-4 h-4" />Witaj!
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Twoja firma w Google,<br /><span className="text-emerald-700">ale bez strony WWW?</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Znalezlismy <strong className="text-slate-900">{business.name}</strong> w Google Maps. Mozemy wygenerowac
            dla Ciebie profesjonalna strone w 30 sekund — kliknij przycisk ponizej i zobacz, jak by wygladala.
          </p>
        </div>

        {/* Business preview */}
        <Card className="overflow-hidden mb-8 shadow-sm">
          <div className="grid sm:grid-cols-3">
            <div className="aspect-video sm:aspect-auto bg-slate-200">
              {photo && <img src={photo} alt={business.name} className="w-full h-full object-cover" />}
            </div>
            <div className="sm:col-span-2 p-6">
              <Badge variant="outline" className="mb-2">{business.category}</Badge>
              <h2 className="text-2xl font-bold mb-2">{business.name}</h2>
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                <StarRating rating={business.rating} />
                <span className="font-medium">{business.rating}</span>
                <span className="text-slate-500">({business.reviews_count} opinii)</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-slate-600 mb-1">
                <MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                <span>{business.address}, {business.city}</span>
              </div>
              {business.phone && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{business.phone}</span>
                </div>
              )}
              {business.description && <p className="text-sm text-slate-600 mt-3">{business.description}</p>}
            </div>
          </div>
        </Card>

        {/* CTA: generate */}
        {!landing && (
          <div className="text-center mb-12">
            <Button
              onClick={generateLanding}
              disabled={generating}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 px-8 text-lg"
            >
              {generating ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generuje twoja strone...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" />Pokaz mi moja przykladowa strone</>
              )}
            </Button>
            <p className="text-xs text-slate-500 mt-3">Bezplatnie. Bez rejestracji. Bez karty.</p>
          </div>
        )}
      </section>

      {/* Preview po wygenerowaniu */}
      {landing && (
        <section id="preview" className="max-w-5xl mx-auto px-4 pb-16">
          <Card className="overflow-hidden mb-6 shadow-xl">
            <div className="relative h-72 sm:h-96 overflow-hidden">
              {photo && <img src={photo} alt={business.name} className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
              <div className="absolute inset-0 flex items-end p-8">
                <div className="text-white max-w-3xl">
                  <Badge className="bg-white/20 backdrop-blur text-white mb-3">{business.category}</Badge>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-2">{landing.content.headline}</h2>
                  <p className="text-lg text-white/90">{landing.content.subheadline}</p>
                </div>
              </div>
            </div>
            <CardContent className="p-8 space-y-6">
              {landing.content.about && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">O firmie</h3>
                  <p className="text-slate-700 leading-relaxed">{landing.content.about}</p>
                </div>
              )}
              {landing.content.services?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Nasze uslugi</h3>
                  <ul className="space-y-2">
                    {landing.content.services.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-700"><Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {landing.content.why_us?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Dlaczego my</h3>
                  <ul className="space-y-2">
                    {landing.content.why_us.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-700"><Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              {landing.content.testimonial && (
                <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-700">
                  "{landing.content.testimonial}"
                </blockquote>
              )}
            </CardContent>
          </Card>

          {/* CTA: aktywacja */}
          <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0 shadow-xl">
            <CardContent className="p-8 sm:p-10 text-center space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold">Podoba Ci sie? Aktywuj te strone juz dzis</h2>
              <p className="text-emerald-50 max-w-2xl mx-auto">
                Otrzymasz adres <code className="bg-white/20 px-2 py-0.5 rounded">{business.name?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.lokalnie.pro</code>,
                pelna kontrole nad zawartoscia w edytorze, i opcjonalnie rezerwacje online Slotify. Anuluj w kazdej chwili.
              </p>
              {packages?.starter && (
                <div className="flex items-center justify-center gap-2 text-3xl font-bold">
                  <span>{packages.starter.price_display}</span>
                </div>
              )}

              {!showActivate ? (
                <Button onClick={() => setShowActivate(true)} size="lg" variant="secondary" className="bg-white text-emerald-700 hover:bg-slate-100 h-14 px-8">
                  Aktywuj moja strone<ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <form onSubmit={handleActivate} className="max-w-sm mx-auto space-y-3 text-left">
                  <Input
                    required
                    type="email"
                    placeholder="Email"
                    value={activateForm.email}
                    onChange={(e) => setActivateForm({ ...activateForm, email: e.target.value })}
                    className="bg-white text-slate-900"
                  />
                  <Input
                    required
                    type="password"
                    placeholder="Haslo"
                    minLength={8}
                    value={activateForm.password}
                    onChange={(e) => setActivateForm({ ...activateForm, password: e.target.value })}
                    className="bg-white text-slate-900"
                  />
                  <Input
                    required
                    type="password"
                    placeholder="Powtorz haslo"
                    minLength={8}
                    value={activateForm.confirmPassword}
                    onChange={(e) => setActivateForm({ ...activateForm, confirmPassword: e.target.value })}
                    className="bg-white text-slate-900"
                  />
                  <p className="text-xs text-emerald-100">
                    Kontynuując, akceptujesz{" "}
                    <Link to="/regulamin" target="_blank" className="underline">Regulamin</Link>
                    {" "}i{" "}
                    <Link to="/polityka-prywatnosci" target="_blank" className="underline">Politykę Prywatności</Link>.
                  </p>
                  <Button type="submit" disabled={activating} size="lg" variant="secondary" className="w-full bg-white text-emerald-700 hover:bg-slate-100 h-12">
                    {activating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Aktywuje...</> : <>Przejdz do platnosci<ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>
                </form>
              )}
              {packages?.business && (
                <p className="text-xs text-emerald-100">Mozesz tez wybrac plan <Link to={`/pricing?business_id=${business.business_id}`} className="underline">Business {packages.business.price_display}</Link> z dodatkowymi funkcjami.</p>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      <footer className="border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-500">
        lokalnie.pro &middot; Strona wygenerowana sztuczna inteligencja. Jezeli to nie Twoja firma — zignoruj te wiadomosc.
      </footer>
    </div>
  );
}
